import React, { useState } from 'react';
import { RefreshCw, Settings, Eye, EyeOff } from 'lucide-react';
import { useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { trpc } from '@/lib/api/trpc';

import { MountPointManager } from '../plugins/components/mount-point-manager';
import { CreateMountDialog } from '../plugins/components/create-mount-dialog';
import { MigrateMountDialog } from '../plugins/components/migrate-mount-dialog';
import { CleanupOrphansDialog } from '../plugins/components/cleanup-orphans-dialog';
import { PluginUpdateBanner } from './components/plugin-update-banner';

/**
 * Mount Points Management - Storefront View
 * Reuses the plugins mount point manager for the storefront tab
 */
import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';

export default withRequiredPermission(StorefrontMountPointsPage, PERMISSIONS.STORES_VIEW);

function StorefrontMountPointsPage() {
  const { id } = useParams<{ id: string }>();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showDirectInstances, setShowDirectInstances] = useState(false);
  const utils = trpc.useUtils();

  // Fetch mount points (lightweight - no instances)
  const { data: mountPointsData } = trpc.plugins.listMountPoints.useQuery(
    {
      storeId: id!,
      includeDirectInstanceRoutes: showDirectInstances,
    },
    { enabled: !!id },
  );

  // Fetch available instances separately (only when needed)
  const { data: instancesData } = trpc.plugins.listAvailableInstances.useQuery(
    { storeId: id! },
    { enabled: !!id },
  );

  // Combine data for backward compatibility
  const data = {
    mountPoints: mountPointsData?.mountPoints || [],
    availableInstances: instancesData || [],
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await utils.plugins.listMountPoints.invalidate();
    } finally {
      setIsRefreshing(false);
    }
  };

  if (!id) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <p className="text-muted-foreground">No store selected</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col space-y-4 sm:space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <div>
          <h1 className="text-xl font-semibold sm:text-2xl">Mount Points</h1>
          <p className="text-muted-foreground text-sm">
            Manage routes and switch between plugin versions/configs
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setShowDirectInstances(!showDirectInstances)}
            variant="outline"
            size="sm"
            className="gap-2"
          >
            {showDirectInstances ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            {showDirectInstances ? 'Hide' : 'Show'} Direct Routes
            {!showDirectInstances && (
              <Badge variant="secondary" className="ml-1">
                Hidden
              </Badge>
            )}
          </Button>
          <CreateMountDialog id={id} onSwitchToMarketplace={() => {}} />
          <MigrateMountDialog id={id} mountPoints={data?.mountPoints || []} />
          <CleanupOrphansDialog id={id} />
          <Button onClick={handleRefresh} disabled={isRefreshing} variant="outline">
            <RefreshCw className={`mr-2 h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Plugin Update Banner */}
      {/* <PluginUpdateBanner storeId={id} /> */}

      <MountPointManager id={id} />
    </div>
  );
}
